import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { Country } from "country-state-city";
import { Field, Form, Formik } from "formik";
import React from "react";
import * as Yup from "yup";
import EditCandidateIco from "../../../../assets/icons/EditCandidateIco";
import XCloseIco from "../../../../assets/icons/XCloseIco";
import CommonButton from "../../../../component/Button";
import CommonTextField from "../../../../component/Input";
import DropDownSelect from "../../../../component/Select";
import { CommonGrid2 } from "../../../common-components/StyledComponents";

export default function EditCandidateProfile({
  open,
  handleClose,
  handleEditCandidateCloseBtn,
  editDialog,
  matchedData,
  handleSubmit,
  candidateDetailsEdit,
}) {
  const initialValues = {
    ...(candidateDetailsEdit
      ? {
          candidate_first_name:
            matchedData?.personal_details?.candidate_first_name || "",
          candidate_middle_name:
            matchedData?.personal_details?.candidate_middle_name || "",
          candidate_last_name:
            matchedData?.personal_details?.candidate_last_name || "",
          source_name: matchedData?.source_name || "",
          source_type: matchedData?.source_type || "",
          source_dropDownData: [
            { name: "Vendor" },
            { name: "Direct" },
            { name: "Jobsite" },
            { name: "Reference" },
          ],
        }
      : {
          candidate_name:
            matchedData?.resume_info?.candidate_name ||
            matchedData?.personal_details?.candidate_name ||
            "",
          live_screening_score:
            matchedData?.resume_info?.live_screening_score || 0,
          willing_to_relocate: (() => {
            const relocate = matchedData?.resume_info?.willing_to_relocate;
            if (!relocate) return "";
            const normalized = relocate.toString().toLowerCase();
            if (normalized === "yes") return "Yes";
            if (normalized === "no") return "No";
            return "";
          })(),
          phone_number:
            matchedData?.resume_info?.phone_number ||
            matchedData?.personal_details?.phone_number ||
            "",
          willing_to_relocate_data: [{ name: "Yes" }, { name: "No" }],
          notice_period: matchedData?.resume_info?.notice_period ?? "",
          visa_status: matchedData?.resume_info?.visa_status?.visa_type || "",
          visa_status_data: [
            { name: "EAD-OPT" },
            { name: "Citizen" },
            { name: "Legal Resident" },
            { name: "Work Permit H1B" },
            { name: "EAD-H4" },
            { name: "Work Permit TN" },
            { name: "EAD L2" },
            { name: "EAD-Legal Resident" },
            { name: "H1B Transfer" },
          ],
        }),
    title:
      matchedData?.resume_info?.inferred_title ||
      matchedData?.personal_details?.inferred_title ||
      "",
    location:
      matchedData?.resume_info?.location?.value ||
      matchedData?.personal_details?.location_details?.location ||
      "",
    min:
      matchedData?.resume_info?.show_rate?.min ||
      matchedData?.personal_details?.show_rate?.min ||
      "",
    max:
      matchedData?.resume_info?.show_rate?.max ||
      matchedData?.personal_details?.show_rate?.max ||
      "",
    rate:
      matchedData?.resume_info?.show_rate?.currency ||
      matchedData?.personal_details?.show_rate?.currency ||
      "",
    email:
      matchedData?.resume_info?.email ||
      matchedData?.personal_details?.email ||
      "",
    country:
      matchedData?.resume_info?.location?.country ||
      matchedData?.personal_details?.location_details?.country ||
      "",
    country_data: Country.getAllCountries().map((country) => ({
      name: country.name,
      isoCode: country.isoCode,
    })),
  };

  const validationSchema = Yup.object({
    ...(candidateDetailsEdit
      ? {
          candidate_first_name: Yup.string().required("First Name is required"),
          source_name: Yup.string().required("Source Name is required"),
          source_type: Yup.string().required("Source Type is required"),
        }
      : {
          candidate_name: Yup.string().required("Name is required"),
          notice_period: Yup.number()
            .typeError("Notice Period must be a number")
            .min(0, "Notice Period cannot be negative"),
        }),
    title: Yup.string().required("Title is required"),
    phone_number: Yup.string()
  .nullable()
  .matches(
    /^[\d\s()+-]+$/,
    "Enter a valid phone number"
  ),
    
    location: Yup.string().required("Location is required"),
    min: Yup.number()
      .typeError("Minimum pay rate must be a number")
      .min(0, "Minimum pay rate cannot be negative")
      .nullable(),
    max: Yup.number()
      .typeError("Maximum pay rate must be a number")
      .nullable()
      .when("min", (min, schema) =>
        min !== undefined && min !== null
          ? schema.moreThan(
              min,
              "Maximum pay rate must be greater than the minimum"
            )
          : schema
      ),
    rate: Yup.string()
      .nullable()
      .when(["min", "max"], {
        is: (min, max) =>
          min !== null &&
          min !== undefined &&
          max !== null &&
          max !== undefined,
        then: (schema) => schema.required("Currency is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    email: Yup.string()
      .nullable()
      .notRequired()
      .test("is-valid-email", "Invalid email address", function (value) {
        if (!value) return true;
        return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
      }),
  });

  const primaryViewData = [
    ...(candidateDetailsEdit
      ? [
          {
            title: "First Name",
            component: "input",
            titleName: "candidate_first_name",
            placeholder: "Enter the First Name",
            size: 4,
          },
          {
            title: "Middle Name",
            component: "input",
            titleName: "candidate_middle_name",
            placeholder: "Enter the Middle Name",
            size: 4,
          },
          {
            title: "Last Name",
            component: "input",
            titleName: "candidate_last_name",
            placeholder: "Enter the Last Name",
            size: 4,
          },
        ]
      : [
          {
            title: "Name",
            component: "input",
            titleName: "candidate_name",
            placeholder: "Enter the Name",
          },
        ]),
    {
      component: "split-part",
      data: [
        {
          title: "Inferred Country",
          component: "single-auto-complete",
          titleName: "country",
          placeholder: "Select Country",
          dropDownData: "country_data",
          placeholderFontSize: "15px",
          size: 3,
          conditionChange: true,
        },
        {
          title: "Inferred Location",
          component: "input",
          titleName: "location",
          placeholder: "Enter the  Location",
          size: 3,
        },
      ],
    },

    {
      title: "Inferred Title",
      component: "input",
      titleName: "title",
      placeholder: "Enter the Inferred Title",
    },

    {
      component: "split-part",
      data: [
        {
          title: "Min Pay Inferred",
          component: "input",
          titleName: "min",
          placeholder: "Enter the Min",
          size: 2.1,
        },
        {
          title: "Max Pay Inferred",
          component: "input",
          titleName: "max",
          placeholder: "Enter the Max",
          size: 2.1,
        },
        {
          title: "Currency",
          component: "dropDown",
          titleName: "rate",
          placeholder: "Select Currency",
          dropDownData: [
            {
              name: "USD",
            },
            { name: "INR" },
          ],
          size: 1.8,
        },
      ],
    },
    {
      title: "Email ID",
      component: "input",
      titleName: "email",
      placeholder: "Enter the email",
    },
    {
      title: "Mobile Number",
      component: "input",
      titleName: "phone_number",
      placeholder: "Enter the Phone Number",
    },
    ...(!candidateDetailsEdit
      ? [
          {
            title: "Willing to Relocate",
            component: "dropDown",
            titleName: "willing_to_relocate",
            placeholder: "Select",
            dropDownData: initialValues.willing_to_relocate_data,
            size: 6,
          },
          {
            title: "Notice Period",
            component: "input",
            titleName: "notice_period",
            placeholder: "Enter Notice Period",
            size: 6,
          },
          {
            title: "Citizenship/Visa Status",
            component: "dropDown",
            titleName: "visa_status",
            placeholder: "Select Visa Status",
            dropDownData: initialValues.visa_status_data,
            size: 6,
          },
        ]
      : []),
    ...(candidateDetailsEdit
      ? [
          {
            title: "Source Type",
            component: "dropDown",
            titleName: "source_type",
            placeholder: "Select the source type",
            size: 6,
            dropDownData: initialValues.source_dropDownData,
          },
          {
            title: "Source Name",
            component: "input",
            titleName: "source_name",
            placeholder: "Enter the Source Name",
            size: 6,
          },
        ]
      : [
          {
            title: "Live Screen Scoring",
            component: "slider",
            titleName: "live_screening_score",
            placeholderFontSize: "15px",
            mandatory: false,
          },
        ]),
  ];

  const handleCurrencyDropdownChange = (
    event,
    values,
    currencyValue,
    setValues
  ) => {
    const exchangeRate = 83;
    function convertUsdToInr(usdValue) {
      return usdValue * exchangeRate;
    }

    function convertInrToUsd(inrValue) {
      return inrValue / exchangeRate;
    }

    const updatedPayRate = {
      [currencyValue]: event.target.value,
      min:
        event.target.value === "INR"
          ? convertUsdToInr(values["min"])
          : convertInrToUsd(values["min"]),
      max:
        event.target.value === "INR"
          ? convertUsdToInr(values["max"])
          : convertInrToUsd(values["max"]),
    };
    setValues((prev) => ({
      ...prev,
      ...updatedPayRate,
    }));
  };

  return (
    <Dialog
      // fullWidth
      maxWidth={"md"}
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "10px",
          maxWidth: "800px",
        },
      }}
    >
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <EditCandidateIco />

        <CommonButton
          onClick={handleClose}
          padding={"10px"}
          background={"rgba(127, 86, 217, 0.04)"}
          border="rgba(127, 86, 217, 0.5)"
          height={"100%"}
          value={<XCloseIco />}
          boxShadow="0px 0px 10px 0px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 4px 0px rgba(0,0,0,0.12)"
        />
      </DialogActions>
      <DialogContent style={{ textAlign: "center", paddingTop: "0px" }}>
        <Typography variant="h3" textAlign={"left"} margin={"1rem 0px"}>
          {"Edit Candidate Information"}
        </Typography>
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleSubmit(values, initialValues)}
          validationSchema={validationSchema}
        >
          {({
            values,
            handleChange,
            setFieldValue,
            errors,
            touched,
            setValues,
          }) => {
            return (
              <Form
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
              >
                <Grid2 spacing={1.5} container marginTop={"2rem"}>
                  <Grid2 spacing={2} container size={12}>
                    {primaryViewData.map((item) =>
                      item.component === "split-part" ? (
                        item.data.map((innerItem) => (
                          <CommonGrid2
                            size={innerItem.size}
                            item
                            gap={"6px"}
                            display={"flex"}
                            flexDirection={"column"}
                            textAlign={"start"}
                          >
                            <Typography fontSize={"14px"}>
                              {innerItem.title}
                            </Typography>
                            {innerItem.component === "input" && (
                              <Field name={innerItem.titleName}>
                                {({ field, form }) => {
                                  return (
                                    <CommonTextField
                                      placeholder={innerItem.placeholder}
                                      placeholderFontSize={"14px"}
                                      padding="10px"
                                      error={
                                        errors[innerItem.titleName] &&
                                        Boolean(errors[innerItem.titleName])
                                      }
                                      value={values[innerItem.titleName]}
                                      onChange={(e) => {
                                        setFieldValue(
                                          innerItem.titleName,
                                          e.target.value
                                        );
                                      }}
                                      helperText={
                                        form.touched[innerItem.titleName] &&
                                        errors[innerItem.titleName]
                                      }
                                      customStyle={{
                                        "& input": {
                                          fontSize: "15px !important",
                                        },
                                      }}
                                    />
                                  );
                                }}
                              </Field>
                            )}
                            {innerItem.component === "dropDown" && (
                              <DropDownSelect
                                marginTop="0px"
                                title={innerItem.title}
                                removeAnimation={false}
                                data={innerItem.dropDownData}
                                error={Boolean(errors[innerItem.titleName])}
                                helperText={errors[innerItem.titleName]}
                                id={innerItem.titleName}
                                value={values[innerItem.titleName]}
                                handleChange={(e) =>
                                  handleCurrencyDropdownChange(
                                    e,
                                    values,
                                    innerItem.titleName,
                                    setValues
                                  )
                                }
                                customStyle={{
                                  "& #demo-simple-select": {
                                    padding: "12px 24px 12px 12px",
                                  },
                                  "& .MuiFormLabel-root": {
                                    width: "30px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }}
                              />
                            )}
                            {innerItem.component === "single-auto-complete" && (
                              <Field name={innerItem.titleName}>
                                {({ field, form }) => {
                                  return (
                                    <Autocomplete
                                      disablePortal
                                      getOptionLabel={(option) => option.name}
                                      options={values[innerItem.dropDownData]}
                                      value={
                                        values[innerItem.titleName]
                                          ? values[innerItem.dropDownData].find(
                                              (option) =>
                                                option.name ===
                                                values[innerItem.titleName]
                                            ) || null
                                          : null
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label={
                                            !values[innerItem.titleName]
                                              ? innerItem.placeholder
                                              : ""
                                          }
                                          error={Boolean(
                                            form.touched[innerItem.titleName] &&
                                              form.errors[innerItem.titleName]
                                          )}
                                          helperText={
                                            form.touched[innerItem.titleName] &&
                                            form.errors[innerItem.titleName]
                                          }
                                        />
                                      )}
                                      onChange={(event, newValue) => {
                                        if (newValue) {
                                          form.setFieldValue(
                                            innerItem.titleName,
                                            newValue.name
                                          );
                                        } else {
                                          form.setFieldValue(
                                            innerItem.titleName,
                                            null
                                          );
                                        }
                                      }}
                                      ListboxProps={{
                                        id: "sigle-autocomplete-list-edit",
                                      }}
                                      sx={{
                                        "& input": {
                                          fontSize: "14px",
                                        },
                                        "& .MuiInputBase-root": {
                                          padding: "5px",
                                        },
                                        "& fieldset": {
                                          border: "1px solid #D0D5DD",
                                          borderRadius: "6px",
                                        },
                                        "& .MuiAutocomplete-option": {
                                          fontSize: "13px !important",
                                          padding: "6px 10px !important",
                                        },
                                        "& label": {
                                          top: "-4px",
                                        },
                                        "& label.MuiInputLabel-shrink": {
                                          top: "0px",
                                        },
                                        "& .Mui-error": {
                                          margin: "2px 0px 0px",
                                          fontWeight: 600,
                                          color: "#d32f2f",
                                        },
                                      }}
                                    />
                                  );
                                }}
                              </Field>
                            )}
                          </CommonGrid2>
                        ))
                      ) : (
                        <CommonGrid2
                          size={item?.size || 6}
                          item
                          gap={"6px"}
                          display={"flex"}
                          flexDirection={"column"}
                          textAlign={"start"}
                        >
                          <Typography fontSize={"14px"}>
                            {item.title}
                          </Typography>
                          {item.component === "input" && (
                            <Field name={item.titleName}>
                              {({ field, form }) => {
                                return (
                                  <CommonTextField
                                    placeholder={item.placeholder}
                                    placeholderFontSize={"14px"}
                                    padding="10px"
                                    error={
                                      errors[item.titleName] &&
                                      Boolean(errors[item.titleName])
                                    }
                                    value={values[item.titleName]}
                                    onChange={(e) => {
                                      setFieldValue(
                                        item.titleName,
                                        e.target.value
                                      );
                                    }}
                                    helperText={errors[item.titleName]}
                                    customStyle={{
                                      "& input": {
                                        fontSize: "15px !important",
                                      },
                                    }}
                                  />
                                );
                              }}
                            </Field>
                          )}
                          {item.component === "dropDown" && (
                            <DropDownSelect
                              marginTop="0px"
                              title={item.title}
                              removeAnimation={true}
                              data={item.dropDownData}
                              error={Boolean(errors[item.titleName])}
                              helperText={errors[item.titleName]}
                              id={item.titleName}
                              value={values[item.titleName]}
                              handleChange={(e) =>
                                setFieldValue(item.titleName, e.target.value)
                              }
                              customStyle={{
                                "& #demo-simple-select": {
                                  padding: "12px 24px 12px 12px",
                                },
                                "& .MuiFormLabel-root": {
                                  width: "30px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          )}
                          {item.component === "slider" && (
                            <Field name={item.titleName}>
                              {({ field, form }) => {
                                return (
                                  <Slider
                                    min={0}
                                    max={100}
                                    step={0.1}
                                    value={values[item.titleName]}
                                    valueLabelDisplay="auto"
                                    onChange={(_, newValue) =>
                                      setFieldValue(item.titleName, newValue)
                                    }
                                    sx={{
                                      height: "7px",
                                      "& .MuiSlider-rail": {
                                        backgroundColor: "#d1d2d4",
                                      },
                                      "& .MuiSlider-thumb": {
                                        width: 22,
                                        height: 22,
                                        backgroundColor: "#fff",
                                        border: "2px solid #7F56D9",
                                      },
                                      "& .MuiSlider-valueLabel": {
                                        backgroundColor: "#6941C6",
                                        fontSize: "13px",
                                        color: "white",
                                      },
                                    }}
                                  />
                                );
                              }}
                            </Field>
                          )}
                        </CommonGrid2>
                      )
                    )}
                  </Grid2>
                </Grid2>
                <CommonGrid2
                  size={12}
                  gap={"1rem"}
                  display={"flex"}
                  justifyContent={"end"}
                  container
                >
                  <CommonGrid2
                    size={2.5}
                    gap={"1rem"}
                    display={"flex"}
                    justifyContent={"end"}
                    item
                  >
                    <CommonButton
                      value={"Cancel"}
                      variant="outlined"
                      color={"#344054"}
                      border={"#D0D5DD"}
                      fontWeight={"600"}
                      padding={"10px"}
                      marginTop={"1.5rem"}
                      width="100%"
                      onClick={handleEditCandidateCloseBtn}
                      disabled={editDialog.disabled}
                      loading={editDialog.cancelLoading}
                    />
                  </CommonGrid2>
                  <CommonGrid2
                    size={2.5}
                    gap={"1rem"}
                    display={"flex"}
                    justifyContent={"end"}
                    item
                  >
                    <CommonButton
                      value={"Save"}
                      fontWeight={"600"}
                      padding={"10px"}
                      type="submit"
                      marginTop={"1.5rem"}
                      width="100%"
                      disabled={editDialog.disabled}
                      loading={editDialog.saveLoading}
                    />
                  </CommonGrid2>
                </CommonGrid2>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
